import { computed, Ref, unref, UnwrapNestedRefs } from 'vue'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { del, get, post, put } from '@/api/base'
import InvoiceDTO, {
  BatchPaymentTerms,
  CountlessPagination,
  BatchDraftResponse,
  BatchListItem,
  BatchSearchDto,
  SyncOption,
  SyncTarget,
  SyncTargetHistory,
} from '@/models/batch'
import { convertTo } from '@/models/utils/casing'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import moment from 'moment'
import { MaybeRef } from '@vueuse/core'
import { PaginatedRequest } from '@/models/search-location-dto'
import { FilterJobRequest } from '@/api/types/filter-job-request'
import {
  prepareFiltersForFetch,
  serializeFiltersForQueryKey,
} from '@/components/jobs/job-search-page/useFilterJobsQuery'
import type { JobFilter, JobListSort } from '@ironsight/types'
import type { BatchListSort } from '@/components/batches/useBatchSort'
import { currentTimezone } from '@/utils/date-helpers'

const invoicesEndPoint = '/api/v5/Invoices'

export function getById(id: string, signal?: AbortSignal) {
  const source = axios.CancelToken.source()
  const invoice = get<InvoiceDTO>(`${invoicesEndPoint}/${id}`, {
    cancelToken: source.token,
  }).then((res) => convertTo<InvoiceDTO>(res.data))
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })
  return invoice
}

interface SearchBatchesOptions {
  offset?: number
  limit?: number
  query?: string
}

export function search(searchOptions?: SearchBatchesOptions | null, signal?: AbortSignal) {
  const source = axios.CancelToken.source()
  const invoice = get<PaginatedRequest<BatchListItem>>(`${invoicesEndPoint}/`, {
    params: {
      offset: searchOptions?.offset,
      limit: searchOptions?.limit,
      query: searchOptions?.query,
    },
    cancelToken: source.token,
  }).then((res) => convertTo<PaginatedRequest<BatchListItem>>(res.data))
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })
  return invoice
}

export function filteredSearch(
  filters: FilterJobRequest[],
  sortBy: string,
  sortDesc: boolean,
  offset = 0,
  limit = 100,
  signal?: AbortSignal,
) {
  const endpoint = `${invoicesEndPoint}/query`
  const source = axios.CancelToken.source()

  const response = post(endpoint, {
    data: {
      filters,
      sort_by: sortBy,
      sort_desc: sortDesc,
      offset,
      limit,
    },
    config: {
      signal,
    },
    cancelToken: source.token,
  }).then((res) =>
    convertTo<CountlessPagination<BatchSearchDto>>(res.data, [
      { key: 'custom_data', onlyDeep: true },
    ]),
  )

  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })

  return response
}

export function filteredSearchCount(filters: FilterJobRequest[], signal?: AbortSignal) {
  const endpoint = `${invoicesEndPoint}/count`
  const source = axios.CancelToken.source()

  const response = post(endpoint, {
    data: {
      filters,
    },
    config: {
      signal,
    },
    cancelToken: source.token,
  })

  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })

  return response
}

function getCustomerNameRecommendationForInvoiceSync(signal?: AbortSignal) {
  const source = axios.CancelToken.source()
  const results = get<string[]>(`${invoicesEndPoint}/sync/customer-name-recommendation`).then(
    (res) => res.data as string[],
  )
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })
  return results
}

export function useGetCustomerNameRecommendationForInvoiceSync() {
  const queryKey = ['invoice', 'sync', 'customer-name-recommendation']
  return useQuery({
    queryKey,
    queryFn: ({ signal }) => getCustomerNameRecommendationForInvoiceSync(signal),
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
  })
}

function updateInvoiceSyncCustomerName(invoiceId: string, targetId: string, customerName?: string) {
  let url = `${invoicesEndPoint}/${invoiceId}/sync/customer-name/?targetId=${targetId}`
  if (customerName) {
    const encodedCustomerName = encodeURIComponent(customerName)
    url += `&customerName=${encodedCustomerName}`
  }
  return put(url, {})
}

export function useUpdateInvoiceSyncCustomerNameMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (variables: { invoiceId: string; targetId: string; customerName?: string }) =>
      updateInvoiceSyncCustomerName(
        variables.invoiceId,
        variables.targetId,
        variables.customerName,
      ),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries(['invoice', 'sync', 'customer-name-recommendation']),
        queryClient.invalidateQueries(['invoice', variables.invoiceId, 'sync-targets']),
      ])
    },
  })
}

export interface CustomDataWithSchema {
  schema_id: number
  data: Record<string, any>
}

export function create(
  id: string | null, // If null, the backend will auto populate this field with a random UUID
  customDataPerApp: Record<string, CustomDataWithSchema> | null,
  invoiceNumber: string | null,
  invoiceDate: moment.Moment,
  paymentTerms: BatchPaymentTerms | null,
  dueDate: moment.Moment | null,
  jobIds: string[] | null,
  ownerId: string,
  memo: string | null,
) {
  return post(`${invoicesEndPoint}/`, {
    data: {
      id,
      custom_data: customDataPerApp,
      invoice_number: invoiceNumber,
      invoice_date: invoiceDate,
      payment_terms: paymentTerms,
      due_date: dueDate,
      job_ids: jobIds,
      owner_id: ownerId,
      memo,
    },
  })
}

export function update(
  id: string,
  customDataPerApp: Record<string, CustomDataWithSchema> | null,
  invoiceNumber?: string | null,
  invoiceDate?: moment.Moment | null,
  paymentTerms?: BatchPaymentTerms | null,
  dueDate?: moment.Moment | null,
  jobIds?: string[] | null,
  memo?: string | null,
) {
  return put(`${invoicesEndPoint}/${id}`, {
    data: {
      invoice_number: invoiceNumber,
      invoice_date: invoiceDate,
      payment_terms: paymentTerms,
      due_date: dueDate,
      job_ids: jobIds,
      memo,
      custom_data: customDataPerApp,
    },
  })
}

export function exportInvoiceCsv(invoiceId: string, timeZone: string) {
  return get(`${invoicesEndPoint}/${invoiceId}/export/csv`, {
    params: {
      TimeZone: timeZone,
    },
    responseType: 'blob',
  })
}

export function exportInvoicePdf(invoiceId: string, timeZone: string) {
  return get(`${invoicesEndPoint}/${invoiceId}/export/pdf`, {
    params: {
      TimeZone: timeZone,
    },
    responseType: 'blob',
  })
}

export function addJobsToInvoice(id: string, jobIds?: string[] | null) {
  return put(`${invoicesEndPoint}/${id}/jobs/add`, {
    data: {
      job_ids: jobIds,
    },
  })
}

export function removeJobsFromInvoice(id: string, jobIds?: string[] | null) {
  return del(`${invoicesEndPoint}/${id}/jobs/delete`, {
    data: {
      job_ids: jobIds,
    },
  })
}

export function removeJobsFromTheirInvoices(jobIds?: string[] | null) {
  return put(`${invoicesEndPoint}/jobs/delete`, {
    data: {
      job_ids: jobIds,
    },
  })
}

export function requestBatchSync(invoiceId: string, targetId: string, customerName?: string) {
  let url = `${invoicesEndPoint}/${invoiceId}/sync-targets/${targetId}`
  if (customerName) {
    const encodedCustomerName = encodeURIComponent(customerName)
    url += `?customerName=${encodedCustomerName}`
  }

  url += url.includes('?') ? `&timezone=${currentTimezone()}` : `?timezone=${currentTimezone()}`

  return post(url, {})
}

export function getSyncOptions() {
  const options = get<Array<SyncOption>>(`${invoicesEndPoint}/sync-options`).then((res) =>
    convertTo<Array<SyncOption>>(res.data),
  )
  return options
}

export interface InvoicingEntitlementResponse {
  hasEntitlement: boolean
}

export function useInvoiceQuery(invoiceId?: MaybeRef<string | null | undefined>) {
  const computedId = computed(() => unref(invoiceId))
  const queryKey = computed(() => ['invoice', computedId.value])
  const enabled = computed(() => {
    return computedId.value !== null && computedId.value !== undefined
  })
  return useQuery({
    queryKey,
    queryFn: ({ signal }) => getById(computedId.value!, signal),
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
    enabled,
  })
}

export function useSearchInvoicesQuery(query: MaybeRef<UnwrapNestedRefs<SearchBatchesOptions>>) {
  const { data, error, isLoading, isFetched, isFetching, refetch, isRefetching } = useQuery({
    queryKey: ['invoices', 'search', query],
    queryFn: ({ signal }) => search(unref(query), signal),
    staleTime: moment.duration(30, 'seconds').asMilliseconds(),
    retry: false,
    keepPreviousData: true,
    enabled: true,
  })

  const invoices = computed(() => data.value?.data)

  const totalPages = computed<number | null | undefined>(() => {
    return data?.value?.meta?.totalPages
  })
  const totalItems = computed<number | null | undefined>(() => {
    return data?.value?.meta?.total
  })

  const hasMore = computed<boolean>(() => {
    const hasNoMetadata = !data?.value?.meta
    const hasNoTotalPages = !data?.value?.meta?.totalPages

    if (hasNoMetadata || hasNoTotalPages) {
      return true
    }

    return Boolean(
      data?.value?.meta?.totalPages &&
      data?.value?.meta?.currentPage < data?.value?.meta?.totalPages,
    )
  })

  return {
    invoices,
    totalPages,
    totalItems,
    hasMore,
    error,
    isLoading,
    isFetched,
    isFetching,
    isRefetching,
    reFetch: refetch,
  }
}

export function useFilteredInvoicesQuery(
  filters: Ref<JobFilter[]>,
  sorting: Ref<BatchListSort>,
  page: Ref<number>,
  itemsPerPage: Ref<number>,
  options?: { enabled: boolean | Ref<boolean> },
) {
  const enabled = computed(() => unref(options?.enabled) ?? true)
  const queryKey = computed(() => [
    'invoices_filtered',
    serializeFiltersForQueryKey(filters.value),
    sorting.value.field,
    sorting.value.direction,
    page.value,
    itemsPerPage.value,
  ])

  const queryResult = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      return await filteredSearch(
        prepareFiltersForFetch(filters.value),
        sorting.value.field,
        sorting.value.direction === 'desc',
        (page.value - 1) * itemsPerPage.value,
        itemsPerPage.value,
        signal,
      )
    },
    retry: false,
    keepPreviousData: true,
    enabled,
    refetchInterval: (data, query) => {
      return enabled.value ? 30_000 : false
    },
  })

  const refetch: typeof queryResult.refetch = (...args) => {
    return queryResult.refetch(...args)
  }

  return {
    ...queryResult,
    refetch,
  }
}

export function useInvoiceCount(
  filters: Ref<JobFilter[]>,
  options?: { enabled: boolean | Ref<boolean> },
) {
  const queryKey = computed(() => ['invoices_filtered', serializeFiltersForQueryKey(filters.value)])

  const enabled = computed(() => unref(options?.enabled) ?? true)

  const {
    data,
    refetch: countRefetch,
    isLoading: countLoading,
  } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      return await filteredSearchCount(prepareFiltersForFetch(filters.value), signal)
    },
    retry: false,
    keepPreviousData: true,
    enabled,
  })

  const count = computed<number>(() => {
    return data.value?.data.count ?? 0
  })

  return {
    count,
    countLoading,
    countRefetch,
  }
}

export function useCreateInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (variables: {
      id: string | null
      invoiceNumber: string | null
      invoiceDate: moment.Moment
      paymentTerms: BatchPaymentTerms | null
      dueDate: moment.Moment | null
      jobIds: string[] | null
      ownerId: string
      memo: string | null
      customDataPerApp: Record<string, CustomDataWithSchema> | null
    }) =>
      create(
        variables.id,
        variables.customDataPerApp,
        variables.invoiceNumber,
        variables.invoiceDate,
        variables.paymentTerms,
        variables.dueDate,
        variables.jobIds,
        variables.ownerId,
        variables.memo,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries(['invoice'])
    },
  })
}

export function useUpdateInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation<
    AxiosResponse,
    AxiosError,
    {
      id: string
      invoiceNumber?: string | null
      invoiceDate?: moment.Moment | null
      paymentTerms?: BatchPaymentTerms | null
      dueDate?: moment.Moment | null
      jobIds?: string[]
      memo?: string | null
      customDataPerApp: Record<string, CustomDataWithSchema> | null
    }
  >({
    mutationFn: (variables) =>
      update(
        variables.id,
        variables.customDataPerApp,
        variables.invoiceNumber,
        variables.invoiceDate,
        variables.paymentTerms,
        variables.dueDate,
        variables.jobIds,
        variables.memo,
      ),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries(['invoice', variables.id])
    },
  })
}

export function useAddJobsToInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (variables: { id: string; jobIds?: string[] }) =>
      addJobsToInvoice(variables.id, variables.jobIds),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries(['invoice', variables.id])
    },
  })
}

export function useRemoveJobsFromInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (variables: { id: string; jobIds?: string[] }) =>
      removeJobsFromInvoice(variables.id, variables.jobIds),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries(['invoice', variables.id])
    },
  })
}

export function useRemoveJobsFromTheirInvoicesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (variables: { jobIds?: string[] }) => removeJobsFromTheirInvoices(variables.jobIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries(['invoice', 'search'])
    },
  })
}

export function useRequestBatchSync() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (variables: { invoiceId: string; targetId: string; customerName?: string }) =>
      requestBatchSync(variables.invoiceId, variables.targetId, variables.customerName),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries(['invoice', variables.invoiceId, 'sync-targets'])
    },
  })
}

export function useGetSyncOptionsQuery() {
  const queryKey = computed(() => ['invoice', 'syncOptions'])
  return useQuery({
    queryKey,
    queryFn: () => getSyncOptions(),
    staleTime: moment.duration(60, 'minutes').asMilliseconds(),
  })
}

function getInvoiceSyncTarget(invoiceId: string, signal?: AbortSignal) {
  const source = axios.CancelToken.source()
  const invoice = get<SyncTarget[]>(`${invoicesEndPoint}/${invoiceId}/sync-targets`, {
    cancelToken: source.token,
  }).then((res) => convertTo<SyncTarget[]>(res.data, [{ key: 'custom_data', onlyDeep: true }]))
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })
  return invoice
}

export function useInvoiceSyncTargetsQuery(
  invoiceId?: MaybeRef<string | null | undefined>,
  options?: { refetchInterval?: MaybeRef<number | false> },
) {
  const computedId = computed(() => unref(invoiceId))
  const queryKey = computed(() => ['invoice', computedId.value, 'sync-targets'])
  const enabled = computed(() => {
    return computedId.value !== null && computedId.value !== undefined
  })
  return useQuery({
    queryKey,
    queryFn: ({ signal }) => getInvoiceSyncTarget(computedId.value!, signal),
    enabled,
    refetchInterval: computed(
      () => unref(options?.refetchInterval) ?? moment.duration(1, 'minutes').asMilliseconds(),
    ),
  })
}

function getInvoiceSyncTargetHistory(invoiceId: string, targetId: string, signal?: AbortSignal) {
  const source = axios.CancelToken.source()
  const invoice = get<SyncTargetHistory[]>(
    `${invoicesEndPoint}/${invoiceId}/sync-targets/${targetId}/history`,
    {
      cancelToken: source.token,
    },
  ).then((res) => convertTo<SyncTargetHistory[]>(res.data))
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })
  return invoice
}

export function useInvoiceSyncTargetHistoryQuery(
  invoiceId?: MaybeRef<string | null | undefined>,
  targetId?: MaybeRef<string | null | undefined>,
) {
  const computedInvoiceId = computed(() => unref(invoiceId))
  const computedTargetId = computed(() => unref(targetId))
  const queryKey = computed(() => [
    'invoice',
    computedInvoiceId.value,
    'sync-targets',
    computedTargetId.value,
    'history',
  ])
  const enabled = computed(() => {
    return computedInvoiceId.value !== null && computedTargetId.value !== undefined
  })
  return useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      getInvoiceSyncTargetHistory(computedInvoiceId.value!, computedTargetId.value!, signal),
    enabled,
    refetchInterval: moment.duration(1, 'minutes').asMilliseconds(),
  })
}

function getInvoiceDraft(batchName: string, jobIds: string[], signal?: AbortSignal) {
  const source = axios.CancelToken.source()
  const draft = post(`${invoicesEndPoint}/draft`, {
    data: {
      batch_name: batchName,
      job_ids: jobIds,
    },
    cancelToken: source.token,
  }).then((res) =>
    convertTo<BatchDraftResponse>(res.data, [{ key: 'custom_data', onlyDeep: true }]),
  )
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })
  return draft
}

export function useInvoiceDraftQuery(
  batchNumber: MaybeRef<string | null | undefined>,
  jobIds: MaybeRef<string[]>,
) {
  const computedBatchNumber = computed(() => unref(batchNumber))
  const computedJobIds = computed(() => unref(jobIds))
  const queryKey = computed(() => [
    'invoice',
    'draft',
    computedBatchNumber.value,
    computedJobIds.value,
  ])
  const enabled = computed(() => {
    return (
      computedBatchNumber.value !== null &&
      computedBatchNumber.value !== undefined &&
      computedBatchNumber.value !== ''
    )
  })
  return useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      getInvoiceDraft(computedBatchNumber.value!, computedJobIds.value, signal),
    staleTime: 0,
    enabled,
  })
}
